import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AtSign,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Copy,
  RefreshCw,
  Trash2,
  Send,
  ShieldCheck,
  AlertTriangle,
  Info,
} from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const STATUS_LABEL = {
  not_started: "Awaiting DNS records",
  pending: "Pending — awaiting DNS",
  verified: "Verified & sending",
  failed: "Verification failed",
  temporary_failure: "Temporary failure — try again",
};

export default function PortalEmail() {
  const [me, setMe] = useState(null);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    portalMe().then(setMe).catch(() => {});
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const { data } = await portalApi.get("/portal/email-domain");
      setState(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load email settings.");
    } finally {
      setLoading(false);
    }
  };

  const create = async ({ domain, localPart }) => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/email-domain", {
        domain,
        local_part: localPart || "hello",
      });
      setState(data);
      toast.success("Domain registered. Add the DNS records below, then hit Verify.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't register your domain.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/email-domain/verify", {});
      setState(data);
      if (data.status === "verified") {
        toast.success("Your domain is verified. You're all set!");
      } else {
        toast.info("Not verified yet — Resend is still checking your DNS. Try again in a minute.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verify failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your custom email domain? Your venue emails will fall back to Swingle's domain.")) return;
    setBusy(true);
    try {
      await portalApi.delete("/portal/email-domain");
      await refresh();
      toast.success("Domain removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove domain.");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async (to) => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/email-domain/test", { to });
      toast.success(`Sent from ${data.sender}. Check your inbox.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed.");
    } finally {
      setBusy(false);
    }
  };

  const status = state?.status || "not_started";

  return (
    <PortalShell me={me} testId="portal-email-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3 flex items-center gap-2">
            <AtSign size={14} /> Email settings
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Your sending domain</h1>
          <p className="mt-4 text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
            Send tickets, memberships and review requests from{" "}
            <span className="text-[#F5F5F0]">your own domain</span> instead of Swingle&apos;s.
            Buyers see your brand — no{" "}
            <span className="italic">&quot;via Swingle&quot;</span>, no shared reputation.
            Takes about 5 minutes to set up.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#A3A3A3]">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : state?.domain ? (
          <ConfiguredCard
            state={state}
            status={status}
            busy={busy}
            onVerify={verify}
            onRemove={remove}
            onTest={sendTest}
            onRefresh={refresh}
          />
        ) : (
          <NotStartedCard onCreate={create} busy={busy} />
        )}

        <FaqPanel />
      </div>
    </PortalShell>
  );
}

// -------------------------------------------------------------------------------------------------

function NotStartedCard({ onCreate, busy }) {
  const [domain, setDomain] = useState("");
  const [localPart, setLocalPart] = useState("hello");

  const submit = (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    onCreate({ domain: domain.trim(), localPart: localPart.trim() });
  };

  return (
    <form
      onSubmit={submit}
      data-testid="email-domain-create-form"
      className="border border-white/10 bg-black p-6 md:p-8 space-y-5"
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-2">
          Add your domain
        </div>
        <h2 className="text-2xl md:text-3xl font-black tracking-tighter">Step 1 — Register with Resend</h2>
        <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">
          Enter a subdomain (recommended) or root domain you own. Using a subdomain like{" "}
          <span className="text-[#F5F5F0]">mail.yourvenue.com</span> keeps your existing email
          working safely.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-3 items-end">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">
            From address prefix
          </label>
          <input
            value={localPart}
            onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
            data-testid="email-domain-local-part"
            className="w-full h-11 bg-black border border-white/20 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5] text-sm"
            placeholder="hello"
            required
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">
            Your domain
          </label>
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value.toLowerCase())}
            data-testid="email-domain-input"
            className="w-full h-11 bg-black border border-white/20 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5] text-sm"
            placeholder="mail.yourvenue.com"
            required
          />
        </div>
      </div>

      <div className="text-[11px] text-[#A3A3A3]">
        Preview:{" "}
        <span className="text-[#D9A6F5] font-mono">
          {localPart || "hello"}@{domain || "mail.yourvenue.com"}
        </span>
      </div>

      <div className="pt-3 border-t border-white/10 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={busy || !domain}
          data-testid="email-domain-create-submit"
          className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />}
          Register &amp; get DNS records
        </button>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
          Nothing goes live yet — you&apos;ll add DNS records in the next step.
        </span>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------------------------------

function ConfiguredCard({ state, status, busy, onVerify, onRemove, onTest, onRefresh }) {
  const [testTo, setTestTo] = useState("");
  return (
    <>
      <div
        className={`border p-6 md:p-8 ${
          status === "verified"
            ? "border-[#D9A6F5] bg-[#D9A6F5]/5"
            : status === "failed"
            ? "border-red-500/60 bg-red-500/5"
            : "border-amber-400/50 bg-amber-400/5"
        }`}
        data-testid={`email-domain-status-${status}`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className={`text-[10px] uppercase tracking-[0.28em] mb-2 flex items-center gap-2 ${
                status === "verified"
                  ? "text-[#D9A6F5]"
                  : status === "failed"
                  ? "text-red-400"
                  : "text-amber-400"
              }`}
            >
              {status === "verified" ? (
                <CheckCircle2 size={13} />
              ) : status === "failed" ? (
                <AlertTriangle size={13} />
              ) : (
                <Loader2 size={13} />
              )}
              {STATUS_LABEL[status] || status}
            </div>
            <div className="text-2xl md:text-3xl font-black tracking-tighter text-[#F5F5F0]">
              {state.from_address}
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3]">
              Domain: {state.domain}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {status !== "verified" && (
              <button
                type="button"
                onClick={onVerify}
                disabled={busy}
                data-testid="email-domain-verify"
                className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-5 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                Verify DNS
              </button>
            )}
            <button
              type="button"
              onClick={onRefresh}
              disabled={busy}
              data-testid="email-domain-refresh"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <RefreshCw size={11} /> Refresh
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              data-testid="email-domain-remove"
              className="inline-flex items-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500 hover:text-black px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Trash2 size={11} /> Remove
            </button>
          </div>
        </div>
      </div>

      {/* DNS records */}
      {state.records?.length > 0 && (
        <div className="mt-6 border border-white/10 bg-black p-6 md:p-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-2">
            Step 2 — Add these DNS records
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-[#F5F5F0]">
            At your domain registrar (Cloudflare, GoDaddy, 123-reg…)
          </h2>
          <p className="mt-2 text-[13px] text-[#A3A3A3] leading-relaxed">
            Add each record below exactly as shown. Most registrars publish DNS changes within
            15 minutes. Once done, hit <em>Verify DNS</em> above.
          </p>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-[12px] min-w-[720px]" data-testid="email-domain-records-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] text-left border-b border-white/10">
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Name / Host</th>
                  <th className="py-2 pr-4">Value</th>
                  <th className="py-2 pr-4">TTL</th>
                  <th className="py-2 pr-4">Priority</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {state.records.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 align-top">
                    <td className="py-3 pr-4 text-[#F5F5F0] font-bold">{r.type}</td>
                    <td className="py-3 pr-4 text-[#F5F5F0] font-mono break-all">
                      <CopyValue value={r.name} />
                    </td>
                    <td className="py-3 pr-4 text-[#F5F5F0] font-mono break-all max-w-[280px]">
                      <CopyValue value={r.value} />
                    </td>
                    <td className="py-3 pr-4 text-[#A3A3A3]">{r.ttl || "Auto"}</td>
                    <td className="py-3 pr-4 text-[#A3A3A3]">{r.priority ?? "—"}</td>
                    <td className="py-3 pr-4">
                      <RecordStatusPill status={r.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Test email (verified only) */}
      {status === "verified" && (
        <div className="mt-6 border border-white/10 bg-black p-6 md:p-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-2">
            Step 3 — Send a test
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-[#F5F5F0]">
            See exactly what buyers will see
          </h2>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              type="email"
              placeholder="your.email@example.com"
              data-testid="email-domain-test-to"
              className="flex-1 h-11 bg-black border border-white/20 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5] text-sm"
            />
            <button
              type="button"
              disabled={busy || !testTo}
              onClick={() => onTest(testTo)}
              data-testid="email-domain-test-send"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              Send test
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// -------------------------------------------------------------------------------------------------

function RecordStatusPill({ status }) {
  const ok = status === "verified";
  const notStarted = !status || status === "not_started";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-1 border ${
        ok
          ? "border-[#D9A6F5] text-[#D9A6F5]"
          : notStarted
          ? "border-white/20 text-[#A3A3A3]"
          : "border-amber-400/60 text-amber-300"
      }`}
    >
      {ok ? <CheckCircle2 size={9} /> : notStarted ? null : <Loader2 size={9} />}
      {status || "not started"}
    </span>
  );
}

function CopyValue({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-[#666]">—</span>;
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      className="group inline-flex items-start gap-1.5 text-left hover:text-[#D9A6F5]"
    >
      <span className="break-all">{value}</span>
      {copied ? (
        <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-[#D9A6F5]" />
      ) : (
        <Copy size={12} className="mt-0.5 shrink-0 opacity-40 group-hover:opacity-100" />
      )}
    </button>
  );
}

// -------------------------------------------------------------------------------------------------

function FaqPanel() {
  return (
    <div className="mt-12 border border-white/10 p-6 md:p-8" data-testid="email-domain-faq">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-2 flex items-center gap-2">
        <Info size={12} /> Good to know
      </div>
      <div className="space-y-4 text-[13px] text-[#A3A3A3] leading-relaxed">
        <div>
          <div className="text-[#F5F5F0] font-bold mb-1">Use a subdomain, not your root</div>
          <p>
            We recommend <span className="font-mono text-[#F5F5F0]">mail.yourvenue.com</span>{" "}
            or <span className="font-mono text-[#F5F5F0]">tickets.yourvenue.com</span>.
            This keeps your existing email working safely and isolates the sending reputation.
          </p>
        </div>
        <div>
          <div className="text-[#F5F5F0] font-bold mb-1">Where do I add DNS records?</div>
          <p>
            Whoever manages your website usually manages DNS. Common places: Cloudflare, GoDaddy,
            123-reg, Namecheap, Squarespace, Wix. Look for a section called{" "}
            <em>DNS</em>, <em>Domain records</em>, or <em>Advanced DNS</em>.
          </p>
        </div>
        <div>
          <div className="text-[#F5F5F0] font-bold mb-1">How long does verification take?</div>
          <p>
            Usually under 15 minutes after you add the records. Some registrars can take up to
            24 hours. Hit <em>Verify DNS</em> once you&apos;ve added them.
          </p>
        </div>
        <div>
          <div className="text-[#F5F5F0] font-bold mb-1">If verification fails, we fall back</div>
          <p>
            Emails never bounce because of DNS problems. Until verification succeeds (or if
            it lapses later), we&apos;ll keep sending from Swingle&apos;s shared domain so your
            customers always get their tickets.
          </p>
        </div>
      </div>
    </div>
  );
}
